import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, SendBlankIcon, SendFilledIcon } from "@activecollab/components";
import classNames from "classnames";
import { Formik, Form } from "formik";
import { List } from "immutable";
import PropTypes from "prop-types";
import { ChatEditor } from "./ChatEditor.jsx";
import Angie from "../../../../utils/angie.jsx";
import { keyCodes } from "../../../../utils/key_codes.jsx";
import { CustomFormField } from "../../../CustomFormField.jsx";
import { FormikEffect } from "../../../FormikEffect.jsx";
import { useFileHandler } from "../../../Hooks/useFileHandler.jsx";
import { AttachmentsList } from "../../../select_attachments/AttachmentsList.jsx";
import { AttachmentUpload } from "../../../select_attachments/AttachmentUpload.jsx";
import { ChatAttachmentsPicker } from "../../ChatAttachmentsPicker.jsx";
import { useConversationContext } from "../../context/ConversationContext.jsx";

export const MessageForm = ({
  conversationId,
  body,
  attachmentIds,
  filesToAttach,
  filesToDrop,
  onSubmit,
  onCancel,
  onChange,
  onLoadPrevious,
  includeDirtyState,
}) => {
  const { t } = useTranslation();

  const submitButtonRef = useRef(null);
  const chatEditorRef = useRef(null);

  const fileInputRef = useRef(null);

  const { conversation = {} } = useConversationContext();

  const { uploading, uploads, onAttachFromDropbox, onAttachFromGoogleDrive, onUpload, ...fileHandlerRest } =
    useFileHandler({ context: `conversation-${conversationId}` });

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleValidate = useCallback(
    (values) => {
      const errors = {};

      const { body = "", attachments = {} } = values;
      const { filesToAttach = [], filesToDrop = [] } = attachments;

      if (!body.length && !filesToAttach.length && attachmentIds.size <= filesToDrop.length) {
        errors.body = t("Required");
      }

      return errors;
    },
    [attachmentIds.size, t]
  );

  const handleSubmit = useCallback(
    (values, actions) => {
      const { body = "", attachments = {} } = values;
      const attributes = { body };

      if (attachments?.filesToAttach) {
        attributes.attach_uploaded_files = attachments.filesToAttach;
      }

      if (attachments?.filesToDrop) {
        attributes.drop_attached_files = attachments.filesToDrop;
      }

      typeof onSubmit === "function" && onSubmit(attributes);
      actions.resetForm({
        values: {
          body: "",
          attachments: {
            filesToAttach: [],
            filesToDrop: [],
          },
        },
      });
      actions.validateForm();
      chatEditorRef.current?.focus();
    },
    [onSubmit]
  );

  const handleKeyDown = useCallback(
    (event, editor) => {
      const length = editor.getContent({ format: "text" }).length;

      if (event.key === keyCodes.escape) {
        event.preventDefault();
        typeof onCancel === "function" && onCancel();
      } else if (!event.altKey && !event.shiftKey && event.key === keyCodes.enter) {
        event.preventDefault();
        submitButtonRef.current?.click();
      } else if (event.key === keyCodes.up) {
        !length && onLoadPrevious(event, editor);
      }
    },
    [onLoadPrevious, onCancel]
  );

  const handleChange = useCallback(
    (prev, { body, attachments }) => {
      const bodyPlainText = chatEditorRef.current?.getContent({ format: "text" }) || body;
      typeof onChange === "function" && onChange({ body, attachments, bodyPlainText });
    },
    [onChange]
  );

  const handleImagesChange = useCallback(
    (files) => {
      files.forEach(onUpload);
    },
    [onUpload]
  );

  const initialValues = useMemo(
    () => ({
      body,
      attachments: { filesToAttach, filesToDrop },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const autoFocusEditor = useMemo(() => !Angie.functions.isTouchDevice(), []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={handleValidate}
      validateOnMount
    >
      {({ isValid, dirty }) => {
        const disabledButton = !isValid || uploading || (!dirty && includeDirtyState);
        return (
          <Form>
            <div className="tw-flex tw-items-center">
              <FormikEffect onChange={handleChange} />
              <ChatAttachmentsPicker
                openFileWindow={handleOpenFileWindow}
                onAttachFromDropbox={onAttachFromDropbox}
                onAttachFromGoogleDrive={onAttachFromGoogleDrive}
              />
              <CustomFormField name="body">
                {({ field }) => (
                  <ChatEditor
                    ref={chatEditorRef}
                    value={field.value}
                    placeholder={`${t("Message")}...`}
                    autoFocus={autoFocusEditor}
                    onChange={field.onChange}
                    onKeyDown={handleKeyDown}
                    onImagesChange={handleImagesChange}
                    members={conversation.members}
                  />
                )}
              </CustomFormField>
              <IconButton
                type="submit"
                variant={disabledButton ? "text gray" : "text colored"}
                ref={submitButtonRef}
                disabled={disabledButton}
                className="tw-shrink-0"
              >
                {disabledButton ? <SendBlankIcon /> : <SendFilledIcon />}
              </IconButton>
            </div>
            <CustomFormField name="attachments">
              {({ field }) => (
                <AttachmentsList
                  {...fileHandlerRest}
                  attachmentIds={attachmentIds}
                  className={classNames("object_comments_attachments tw-mt-2", {
                    "tw-hidden":
                      !Object.keys(uploads).length &&
                      attachmentIds.size <= field.value.filesToDrop.length &&
                      !field.value.filesToAttach.length,
                  })}
                  filesToAttach={field.value.filesToAttach}
                  filesToDrop={field.value.filesToDrop}
                  onChange={field.onChange}
                  onAttachFromDropbox={onAttachFromDropbox}
                  onAttachFromGoogleDrive={onAttachFromGoogleDrive}
                  uploads={uploads}
                  openFileWindow={handleOpenFileWindow}
                />
              )}
            </CustomFormField>
            <AttachmentUpload
              ref={fileInputRef}
              multiple
              onUpload={onUpload}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

MessageForm.propTypes = {
  conversationId: PropTypes.number.isRequired,
  body: PropTypes.string,
  attachmentIds: PropTypes.instanceOf(List),
  filesToAttach: PropTypes.arrayOf(PropTypes.string),
  filesToDrop: PropTypes.arrayOf(PropTypes.number),
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onLoadPrevious: PropTypes.func,
  includeDirtyState: PropTypes.bool,
};

MessageForm.defaultProps = {
  attachmentIds: List(),
  filesToAttach: [],
  filesToDrop: [],
  includeDirtyState: false,
};
